import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, Trash2, ToggleLeft, ToggleRight, X, Truck, MapPin } from 'lucide-react';
import {
  fetchDrivers,
  fetchDriversCount,
  registerDriver,
  assignDriverToZone,
  toggleDriverActive,
  deleteDriver,
  fetchZones,
} from '../services/api.ts';
import './Drivers.css';

interface FormData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  vehicle_type: string;
  vehicle_number: string;
}

const emptyForm: FormData = { email: '', password: '', first_name: '', last_name: '', phone: '', vehicle_type: '', vehicle_number: '' };

const Drivers: React.FC = () => {
  const [drivers, setDrivers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [showZoneModal, setShowZoneModal] = useState(false);
  const [assigningDriverId, setAssigningDriverId] = useState<number | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | ''>('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const [list, countRes] = await Promise.all([
        fetchDrivers(offset, perPage),
        fetchDriversCount(),
      ]);
      setDrivers(list);
      setTotal(countRes.count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = drivers.filter((d) => {
    const s = `${d.first_name} ${d.last_name} ${d.email} ${d.phone || ''} ${d.zone?.name || ''}`.toLowerCase();
    return s.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(total / perPage);

  const openAdd = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.email.trim() || !form.password || !form.first_name.trim() || !form.last_name.trim()) {
      setFormError('Email, password, first name and last name are required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await registerDriver({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || undefined,
        vehicle_type: form.vehicle_type.trim() || undefined,
        vehicle_number: form.vehicle_number.trim() || undefined,
      });
      setShowModal(false);
      loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to register driver');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await toggleDriverActive(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to toggle active');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this driver?')) return;
    try {
      await deleteDriver(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete driver');
    }
  };

  const openAssignZone = async (driverId: number) => {
    setAssigningDriverId(driverId);
    setSelectedZoneId('');
    try {
      const list = await fetchZones(0, 100);
      setZones(list);
      setShowZoneModal(true);
    } catch {
      alert('Failed to load zones');
    }
  };

  const handleAssignZone = async () => {
    if (!selectedZoneId || !assigningDriverId) return;
    try {
      await assignDriverToZone(assigningDriverId, Number(selectedZoneId));
      setShowZoneModal(false);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to assign zone');
    }
  };

  if (loading) {
    return (
      <div className="drivers-page">
        <div className="drivers-loading"><div className="drivers-spinner" /><p>Loading drivers...</p></div>
      </div>
    );
  }

  return (
    <div className="drivers-page">
      <div className="drivers-header">
        <div className="drivers-header-left">
          <Truck size={22} />
          <h1>Drivers</h1>
          <span className="drivers-count">{total}</span>
        </div>
        <button className="drivers-btn drivers-btn-primary" onClick={openAdd}>
          <Plus size={16} /> Register Driver
        </button>
      </div>

      <div className="drivers-search-bar">
        <Search size={18} />
        <input type="text" placeholder="Search by name, email, phone, zone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </div>

      {error && <div className="drivers-error">{error}</div>}

      <div className="drivers-table-wrap">
        <table className="drivers-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Vehicle</th>
              <th>Zone</th>
              <th>Active</th>
              <th>Available</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="drivers-empty">No drivers found</td></tr>
            ) : filtered.map((d) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td className="drivers-cell-name">{d.first_name} {d.last_name}</td>
                <td>{d.email}</td>
                <td>{d.phone || '\u2014'}</td>
                <td>{d.vehicle_type || '\u2014'}</td>
                <td>
                  {d.zone?.name ? (
                    <span className="drivers-badge drivers-badge-info">{d.zone.name}</span>
                  ) : (
                    <button className="drivers-btn drivers-btn-sm drivers-btn-secondary" onClick={() => openAssignZone(d.id)}>Assign Zone</button>
                  )}
                </td>
                <td>
                  <button className={`drivers-toggle ${d.is_active ? 'drivers-toggle-on' : 'drivers-toggle-off'}`} title="Toggle active" onClick={() => handleToggleActive(d.id)}>
                    {d.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                </td>
                <td>
                  {d.is_available ? (
                    <span className="drivers-badge drivers-badge-success">Yes</span>
                  ) : (
                    <span className="drivers-badge drivers-badge-danger">No</span>
                  )}
                </td>
                <td>
                  <div className="drivers-cell-actions">
                    <button className="drivers-action-btn drivers-action-assign" title="Assign Zone" onClick={() => openAssignZone(d.id)}>
                      <MapPin size={15} />
                    </button>
                    <button className="drivers-action-btn drivers-action-delete" title="Delete" onClick={() => handleDelete(d.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="drivers-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Register Driver Modal */}
      {showModal && (
        <div className="drivers-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="drivers-modal" onClick={e => e.stopPropagation()}>
            <div className="drivers-modal-header">
              <h2>Register Driver</h2>
              <button className="drivers-modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="drivers-modal-body">
              {formError && <div className="drivers-form-error">{formError}</div>}
              <div className="drivers-form-row-half">
                <div className="drivers-form-row">
                  <label>First Name *</label>
                  <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} />
                </div>
                <div className="drivers-form-row">
                  <label>Last Name *</label>
                  <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} />
                </div>
              </div>
              <div className="drivers-form-row">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div className="drivers-form-row">
                <label>Password *</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
              </div>
              <div className="drivers-form-row">
                <label>Phone</label>
                <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div className="drivers-form-row-half">
                <div className="drivers-form-row">
                  <label>Vehicle Type</label>
                  <input type="text" placeholder="e.g. car, bike" value={form.vehicle_type} onChange={e => setForm({...form, vehicle_type: e.target.value})} />
                </div>
                <div className="drivers-form-row">
                  <label>Vehicle Number</label>
                  <input type="text" value={form.vehicle_number} onChange={e => setForm({...form, vehicle_number: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="drivers-modal-footer">
              <button className="drivers-btn drivers-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="drivers-btn drivers-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Register'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Zone Modal */}
      {showZoneModal && (
        <div className="drivers-modal-overlay" onClick={() => setShowZoneModal(false)}>
          <div className="drivers-modal drivers-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="drivers-modal-header">
              <h2>Assign Zone</h2>
              <button className="drivers-modal-close" onClick={() => setShowZoneModal(false)}><X size={20} /></button>
            </div>
            <div className="drivers-modal-body">
              <div className="drivers-form-row">
                <label>Select Zone</label>
                <select value={selectedZoneId} onChange={e => setSelectedZoneId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Select a zone</option>
                  {zones.map((z: any) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
            </div>
            <div className="drivers-modal-footer">
              <button className="drivers-btn drivers-btn-secondary" onClick={() => setShowZoneModal(false)}>Cancel</button>
              <button className="drivers-btn drivers-btn-primary" onClick={handleAssignZone} disabled={!selectedZoneId}>Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
